import React, { FC, useEffect, useState } from "react";
import { useBreakpointValue, Wrap, WrapItem } from "@chakra-ui/react";
import { ArticlePreview } from "sdk/articles";
import { SkeletonArticleCards } from "~/features/Library";
import { ArticleCardType } from "~/features/Library/utils";
import { ArticleWrapper } from "./ArticleWrapper";

const ArticleRowMemo = React.memo(ArticleWrapper);

interface ArticleDefinition {
  slug: string;
  topic: string;
}

interface ArticleRowProps {
  articles: ArticleDefinition[];
}

export const ArticleRow: FC<ArticleRowProps> = ({ articles }) => {
  const [articlePreviews, setArticlePreviews] = useState<ArticlePreview[]>([]);
  const [loading, setLoading] = useState(true);
  const articleCardType: ArticleCardType =
    useBreakpointValue({ base: "mobile", sm: "standard" }) ?? "standard";

  // Fetch all articles based on slug/topic
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      const promises = articles.map(({ slug, topic }) =>
        fetch(`/api/v1/articles/${slug}?topic=${topic}`)
          .then((res) => res.json())
          .catch(() => null),
      );

      const results = await Promise.all(promises);
      const validArticles = results
        .filter(
          (article) =>
            article !== null && article.heroImageSrc && article.topic,
        )
        .map((article) => ({
          _id: article._id,
          heroImageAlt: article.heroImageAlt || "",
          heroImageSrc: article.heroImageSrc,
          isLocked: article.isLocked || false,
          slug: article.slug,
          title: article.title,
          topic: article.topic,
        }));

      setArticlePreviews(validArticles);
      setLoading(false);
    };

    if (articles.length > 0) {
      fetchArticles();
    }
  }, [articles]);

  return (
    <Wrap
      justify={{ base: "center", md: "flex-start" }}
      spacingX={3}
      spacingY={{ base: "10vw", sm: 6 }}
    >
      {loading ? (
        <SkeletonArticleCards noOfRows={1} type="standard" />
      ) : (
        articlePreviews.map((article: ArticlePreview) => (
          <WrapItem
            key={article._id}
            mr={{ base: 0, sm: 3 }}
            w={{ base: "100%", sm: "initial" }}
          >
            <ArticleRowMemo
              article={article}
              key={article._id}
              type={articleCardType}
            />
          </WrapItem>
        ))
      )}
    </Wrap>
  );
};
